import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PersonIcon from '@material-ui/icons/Person';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import React, { useMemo, useState } from 'react';
import ActivityChartModal from 'src/app/components/popupInfocar/ActivityChartModal';
import WarrantyReplacementModal from 'src/app/views/device/WarrantyReplacementModal';
import { getIconStatus, getNormalizedVehicleStatus } from 'src/app/utils/vehicleService';
import { VEHICLE_STATUS_COLOR, VEHICLE_STATUS_LABEL } from '../constants';
import useStyles from '../styles';
import { buildVehicleInfoSections, formatVehicleFieldValue } from '../utils';
import DriverLoginModal from './DriverLoginModal';

const WRAP_FIELDS = new Set(['location']);

const VehicleInfoPanel = ({
  vehicle,
  canWarrantyReplace,
  onWarrantyReplaced,
  onDriverLoginSuccess
}) => {
  const classes = useStyles();
  const [openOilChart, setOpenOilChart] = useState(false);
  const [openWarranty, setOpenWarranty] = useState(false);
  const [warrantyResult, setWarrantyResult] = useState(null);
  const [openDriverLogin, setOpenDriverLogin] = useState(false);
  const [driverModalMode, setDriverModalMode] = useState('login');

  const deviceId = vehicle?.device_id || vehicle?.id;
  const isDriverLoggedIn = Boolean(
    vehicle?.driver_name &&
    vehicle.driver_name.trim() !== '' &&
    vehicle.driver_name.trim() !== '--' &&
    vehicle.driver_name.trim() !== 'Lái xe đăng xuất'
  );

  const normalizedStatus = getNormalizedVehicleStatus(vehicle.device_status);
  const statusIcon = getIconStatus(vehicle.device_status, vehicle.vehicle_type);
  const statusLabel = VEHICLE_STATUS_LABEL[normalizedStatus];
  const statusColor = VEHICLE_STATUS_COLOR[normalizedStatus];
  const sections = buildVehicleInfoSections(vehicle);
  // Giữ tham chiếu ổn định: vehicle đổi liên tục theo MQTT, modal reset form mỗi khi prop device đổi
  const warrantyDevice = useMemo(() => ({ serial: vehicle.serial }), [vehicle.serial]);

  // Tải lại xe khi đóng modal: reload ngay lúc thành công sẽ đổi prop device và reset màn hình kết quả
  const handleCloseWarranty = () => {
    setOpenWarranty(false);
    if (warrantyResult) {
      onWarrantyReplaced && onWarrantyReplaced(warrantyResult);
      setWarrantyResult(null);
    }
  };

  return (
    <>
      <Box className={classes.statusHero}>
        <img src={statusIcon} alt="status" className={classes.statusHeroIcon} />
        <Box>
          <Typography className={classes.statusHeroLabel}>Trạng thái xe</Typography>
          <Chip
            size="small"
            label={statusLabel}
            style={{ backgroundColor: `${statusColor}1f`, color: statusColor, fontWeight: 700 }}
          />
        </Box>
        {canWarrantyReplace && vehicle.serial && (
          <Tooltip title="Đổi thiết bị bảo hành">
            <IconButton
              size="small"
              className={classes.warrantyBtn}
              onClick={() => setOpenWarranty(true)}
            >
              <SwapHorizIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {sections.map(section => (
        <Box key={section.key} className={classes.kvGroup}>
          {section.key === 'driver' ? (
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography className={classes.kvGroupTitle} style={{ marginBottom: 0 }}>
                {section.title}
              </Typography>
              {isDriverLoggedIn ? (
                <Button
                  size="small"
                  className={classes.driverLogoutBtn}
                  onClick={() => {
                    setDriverModalMode('logout');
                    setOpenDriverLogin(true);
                  }}
                  disabled={!deviceId}
                >
                  Đăng xuất
                </Button>
              ) : (
                <Button
                  size="small"
                  className={classes.driverLoginBtn}
                  onClick={() => {
                    setDriverModalMode('login');
                    setOpenDriverLogin(true);
                  }}
                  disabled={!deviceId}
                >
                  Đăng nhập
                </Button>
              )}
            </Box>
          ) : (
            <Typography className={classes.kvGroupTitle}>{section.title}</Typography>
          )}
          {section.items.map(item =>
            item.dataKey === 'activity_chart' ? (
              <Box key={item.dataKey} className={classes.kvRow}>
                <span className={classes.kvLabel}>{item.label}</span>
                <Box display="flex" alignItems="center">
                  <span className={classes.kvValue} style={{ marginRight: 6 }}>
                    {vehicle.oil_cap != null ? `${vehicle.oil_cap} L` : '--'}
                  </span>
                  <Button
                    size="small"
                    className={classes.chartBtn}
                    onClick={() => setOpenOilChart(true)}
                  >
                    Xem biểu đồ
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                key={item.dataKey}
                className={classes.kvRow}
                style={WRAP_FIELDS.has(item.dataKey) ? { alignItems: 'flex-start' } : undefined}
              >
                <span className={classes.kvLabel}>{item.label}</span>
                <span
                  className={
                    WRAP_FIELDS.has(item.dataKey) ? classes.kvValueWrap : classes.kvValue
                  }
                  title={String(formatVehicleFieldValue(vehicle, item.dataKey))}
                >
                  {formatVehicleFieldValue(vehicle, item.dataKey)}
                </span>
              </Box>
            )
          )}
        </Box>
      ))}

      <ActivityChartModal
        open={openOilChart}
        handleClose={() => setOpenOilChart(false)}
        defaultVehicle={vehicle}
      />

      {openWarranty && (
        <WarrantyReplacementModal
          open={openWarranty}
          device={warrantyDevice}
          onClose={handleCloseWarranty}
          onSuccess={setWarrantyResult}
        />
      )}

      {openDriverLogin && (
        <DriverLoginModal
          open={openDriverLogin}
          mode={driverModalMode}
          onClose={() => setOpenDriverLogin(false)}
          vehicle={vehicle}
          onSuccess={() => {
            if (onDriverLoginSuccess) onDriverLoginSuccess();
          }}
        />
      )}
    </>
  );
};

export default VehicleInfoPanel;
